import { readFile, readdir } from 'node:fs/promises';
import { homedir, platform } from 'node:os';
import { join } from 'node:path';
import { execFile } from 'node:child_process';
import { promisify } from 'node:util';
import type { ClaudeAuthReport, ClaudeAuthStatus } from '@augmented/core';

const execFileAsync = promisify(execFile);

// Anything less than this is reported as "expiring_soon" — gives the operator
// enough runway to re-pair before the host goes offline.
const EXPIRING_SOON_MS = 48 * 60 * 60 * 1000; // 48 hours

/**
 * Best-effort detection of the host's Claude Code authentication state.
 *
 * Precedence:
 *   1. ANTHROPIC_API_KEY / ANTHROPIC_AUTH_TOKEN env var → mode=api_key, valid forever
 *   2. macOS: Keychain entry "Claude Code-credentials"
 *   3. Linux: ~/.claude/.credentials.json (or credentials.json)
 *
 * Reported in the host heartbeat so the console can surface a "re-pair"
 * prompt before Claude Code goes silently broken.
 *
 * Returns null if no authentication state can be detected at all (e.g. the
 * host has never logged in and has no API key set).
 */
export async function detectClaudeAuth(): Promise<ClaudeAuthReport | null> {
  if (process.env['ANTHROPIC_API_KEY'] || process.env['ANTHROPIC_AUTH_TOKEN']) {
    return { mode: 'api_key', status: 'valid', expires_at: null };
  }

  const creds = await readOauthCredentials();
  if (!creds) return null;

  return computeSubscriptionStatus(creds);
}

type OauthCreds = {
  expiresAt?: number | string;
  accessToken?: string;
  refreshToken?: string;
};

/**
 * List every path where Claude Code OAuth credentials might live on this
 * host, ordered by preference (calling user's home first, then /home/* on
 * Linux-root). Useful for other CLI code that wants to e.g. copy the creds
 * into the manager's HOME before spawning claude.
 */
export async function findClaudeCredentialsPaths(): Promise<string[]> {
  const candidates: string[] = [
    join(homedir(), '.claude', '.credentials.json'),
    join(homedir(), '.claude', 'credentials.json'),
  ];

  const isLinuxRoot =
    platform() === 'linux'
    && typeof process.getuid === 'function'
    && process.getuid() === 0;

  if (isLinuxRoot) {
    try {
      const entries = await readdir('/home', { withFileTypes: true });
      for (const entry of entries) {
        if (!entry.isDirectory()) continue;
        candidates.push(join('/home', entry.name, '.claude', '.credentials.json'));
        candidates.push(join('/home', entry.name, '.claude', 'credentials.json'));
      }
    } catch {
      // /home doesn't exist or isn't readable
    }
  }

  return candidates;
}

async function readOauthCredentials(): Promise<OauthCreds | null> {
  if (platform() === 'darwin') {
    const fromKeychain = await readMacKeychain();
    if (fromKeychain) return fromKeychain;
  }

  const candidates = await findClaudeCredentialsPaths();

  for (const path of candidates) {
    const parsed = await readJsonSilently(path);
    if (parsed) {
      // Claude Code has historically used several keys at the top level.
      return (parsed.claudeAiOauth ?? parsed.oauth ?? parsed) as OauthCreds;
    }
  }

  return null;
}

async function readMacKeychain(): Promise<OauthCreds | null> {
  try {
    const { stdout } = await execFileAsync(
      'security',
      ['find-generic-password', '-s', 'Claude Code-credentials', '-w'],
      { timeout: 3000 }
    );
    const parsed = JSON.parse(stdout.trim()) as Record<string, unknown>;
    return (parsed.claudeAiOauth ?? parsed) as OauthCreds;
  } catch {
    return null;
  }
}

async function readJsonSilently(path: string): Promise<Record<string, unknown> | null> {
  try {
    const raw = await readFile(path, 'utf-8');
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function computeSubscriptionStatus(creds: OauthCreds): ClaudeAuthReport {
  const expiresAtMs = parseExpiresAt(creds.expiresAt);

  // `expiresAt` on Claude Code subscription creds is the short-lived access
  // token (~8 hours). When a refresh token is present, Claude Code auto-
  // refreshes it silently in the background — the access-token expiry is
  // not a meaningful signal for "needs human action". Only warn on access-
  // token expiry when there's no refresh token to fall back on.
  //
  // Caveat: we don't track refresh-token expiry because Claude Code's
  // credentials format doesn't expose one. Refresh tokens are typically
  // long-lived (months). If one does expire, Claude Code surfaces the
  // failure at the next auto-refresh attempt, which the operator will
  // see in their manager.log. Trading off a rare silent-fail scenario
  // for a much worse "warn on every login" experience.
  const hasRefreshToken = typeof creds.refreshToken === 'string' && creds.refreshToken.trim().length > 0;

  if (expiresAtMs === null) {
    return {
      mode: 'subscription',
      status: 'unknown',
      expires_at: null,
    };
  }

  const msUntilExpiry = expiresAtMs - Date.now();
  let status: ClaudeAuthStatus;
  if (hasRefreshToken) {
    // Even past the access-token expiry we expect auto-refresh to succeed.
    // Only report 'expired' if we're past expiry AND don't have a refresh
    // token — we can't reach that branch here, so always 'valid' when
    // refresh token is present.
    status = 'valid';
  } else {
    status =
      msUntilExpiry <= 0 ? 'expired'
      : msUntilExpiry <= EXPIRING_SOON_MS ? 'expiring_soon'
      : 'valid';
  }

  return {
    mode: 'subscription',
    status,
    expires_at: new Date(expiresAtMs).toISOString(),
  };
}

function parseExpiresAt(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    // Ms-since-epoch or seconds-since-epoch — disambiguate: anything below
    // year 2100 in seconds is <= 4e9, while the same instant in ms is 4e12.
    return raw < 1e12 ? raw * 1000 : raw;
  }
  if (typeof raw === 'string') {
    const parsed = Date.parse(raw);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}
